import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';

// Per-client external SSO uses a single multi-tenant app registration owned
// by Mount Insights. Each client's tenant grants admin consent to it; sign-in
// uses the client's tenant authority. We build a fresh MSAL instance per call
// because the authority varies by client.

const REDIRECT = `${window.location.origin}/auth/external-callback`;
const TENANT_KEY = 'mi-external-tenant';
const CLIENT_ID_KEY = 'mi-external-clientid';
const SLUG_KEY = 'mi-external-slug';

// Scopes the user consents to at sign-in. Identity + the GroupMember.Read.All
// delegated permission so the API can check group membership via Graph using
// the access_token returned alongside the id_token.
export const EXTERNAL_SCOPES = ['openid', 'profile', 'email', 'User.Read', 'GroupMember.Read.All'];

export function buildExternalMsal(tenantId: string, clientId: string): PublicClientApplication {
  const config: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: REDIRECT,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    // External SSO is single-tab and short-lived — sessionStorage avoids
    // leaving Microsoft account state behind across tabs.
    cache: { cacheLocation: 'sessionStorage' },
    system: { loggerOptions: { logLevel: LogLevel.Warning, piiLoggingEnabled: false, loggerCallback: () => {} } },
  };
  return new PublicClientApplication(config);
}

export interface PendingExternal {
  tenantId: string;
  clientId: string;
  slug: string | null;
}

export function stashPending(p: PendingExternal): void {
  sessionStorage.setItem(TENANT_KEY, p.tenantId);
  sessionStorage.setItem(CLIENT_ID_KEY, p.clientId);
  if (p.slug) sessionStorage.setItem(SLUG_KEY, p.slug); else sessionStorage.removeItem(SLUG_KEY);
}

export function readPending(): PendingExternal | null {
  const tenantId = sessionStorage.getItem(TENANT_KEY);
  const clientId = sessionStorage.getItem(CLIENT_ID_KEY);
  if (!tenantId || !clientId) return null;
  return { tenantId, clientId, slug: sessionStorage.getItem(SLUG_KEY) };
}

export function clearPending(): void {
  sessionStorage.removeItem(TENANT_KEY);
  sessionStorage.removeItem(CLIENT_ID_KEY);
  sessionStorage.removeItem(SLUG_KEY);
}

// Kick off an external SSO sign-in. Caller has already retrieved the
// per-client config (tenant_id + app_client_id) from /auth/discover.
export async function beginExternalSignIn(opts: { tenantId: string; appClientId: string; slug: string | null }): Promise<void> {
  const pca = buildExternalMsal(opts.tenantId, opts.appClientId);
  await pca.initialize();
  stashPending({ tenantId: opts.tenantId, clientId: opts.appClientId, slug: opts.slug });
  await pca.loginRedirect({ scopes: EXTERNAL_SCOPES });
}
